import { PipelineConfig } from './types';
import { PipelineFiles, RepoRef } from './paths';
import { DiffLine } from './lineDiff';

/**
 * 프론트엔드 ↔ 백엔드 API 계약.
 *
 * 이 타입들이 `-common` 에 있는 이유: 수정 다이얼로그(프론트)와
 * PipelineService(백엔드)가 같은 모양을 주고받아야 한다. 한쪽에만 두고
 * 다른 쪽이 복제하면 필드 추가 시 조용히 어긋난다.
 */

/** 파일 위치 + 저장소 좌표. 엔티티 annotation 에서 온다. */
export type PipelineLocation = {
  teamTag: string;
  /** `dags-{team}` 아래 폴더 경로 */
  dagFolder: string;
  dagRepo: RepoRef;
  /** DAG 디렉터리와 그 안의 파일 경로들 */
  files: PipelineFiles;
  catalogRepo: RepoRef;
  catalogPath: string;
};

/** 이 파이프라인을 이미 수정 중인 열린 PR. */
export type OpenPipelinePr = {
  number: number;
  url: string;
  title: string;
  branch: string;
  /** 그 브랜치 기준 설정. 이어서 수정할 때 폼 초기값으로 쓴다. */
  configOnBranch: PipelineConfig | null;
  shasOnBranch: PipelineShas;
};

/**
 * 파일 경로 → blob sha. 낙관적 동시성의 기준값이다.
 *
 * 파일 2 개 고정이 아니라 맵인 이유: 새 규약의 파이프라인은 `.py`·conf yaml·
 * SQL·catalog-info 로 최대 4 개 파일이고, 「넣기」 를 끄면 SQL 이 없다. 고정
 * 필드로 두면 켜짐/꺼짐 조합마다 분기가 생긴다.
 */
export type PipelineShas = Record<string, string | null>;

export type EditingContext = {
  pipelineName: string;
  /** 권위 설정 — 카탈로그 투영이 아니라 catalog repo 의 Git 파일에서 읽은 값. */
  config: PipelineConfig;
  source: { shas: PipelineShas; ref: string };
  location: PipelineLocation;
  /** 이 파이프라인을 수정 중인 열린 PR 들. 보통 0개 또는 1개. */
  openPrs: OpenPipelinePr[];
  /**
   * 저장소의 DAG 파일이 `spec.pipelineConfig` 에서 렌더한 결과와 다른가.
   *
   * 가이드는 사람이 직접 파일을 고치는 것도 정상 경로로 둔다. 그 경우 폼
   * 상태(= catalog repo 의 설정)가 낡고, 그대로 저장하면 사람의 수정이
   * **조용히 되돌아간다**. 저장 전에 경고하기 위한 신호다.
   */
  handEdited: boolean;
  /**
   * 무엇이 다른가 — 파일마다 저장소 내용과 「저장하면 들어갈 내용」 의 diff.
   * `handEdited` 만으로는 저장해도 되는지 판단할 수 없었다(사용자 지적).
   */
  handEdits: HandEdit[];
};

/** Backstage 밖에서 고친 DAG 파일 하나. */
export type HandEdit = {
  path: string;
  /** `modified` 내용이 다름 · `missing` 저장소에 없음(저장하면 다시 생긴다) */
  change: 'modified' | 'missing';
  /** 저장소 → Backstage 설정. `-` 줄이 저장하면 사라지는 밖의 수정이다. */
  hunks: DiffLine[][];
};

/** 생성 요청. 수정과 달리 기준 sha 가 없다. */
export type CreatePipelineRequest = {
  ticketNumber: string;
  config: PipelineConfig;
};

export type SavePipelineRequest = {
  ticketNumber: string;
  config: PipelineConfig;
  /** 폼이 불러온 시점의 sha. 불일치 시 409. */
  base: PipelineShas;
  /** `handEdited` 경고를 사용자가 보고 덮어쓰기로 결정했는가. */
  overwriteHandEdits?: boolean;
};

export type DeletePipelineRequest = { ticketNumber: string };

export type PipelinePrResult = {
  dagPrUrl: string;
  dagPrNumber: number;
  catalogPrUrl: string;
  catalogPrNumber: number;
  branch: string;
  /** 두 PR 모두 머지되어야 반영이 완료된다. 카탈로그 PR 은 백엔드가 DAG PR 을 따라 머지한다. */
  requiresBothMerges: true;
  /** 삭제만: 운영계에도 있어 DAG PR 머지 뒤 백엔드가 `/promote` 를 남긴다. */
  promoteOnMerge?: boolean;
};

export type FileConflict = {
  path: string;
  expected: string | null;
  actual: string | null;
};

/** 409 응답 본문. 최신 설정을 함께 주어 프론트가 재로드를 유도할 수 있다. */
export type PipelineConflictResponse = {
  error: 'conflict';
  message: string;
  conflicts: FileConflict[];
  latest: EditingContext;
};

export type AirflowEnv = 'dev' | 'prod';

/** 삭제 확인에 쓰는 최근 실행 요약. */
export type RunSummary = {
  env: AirflowEnv;
  days: number;
  total: number;
  failed: number;
  lastRunAt: string | null;
  lastState: string | null;
  /** 환경이 설정되지 않았거나 조회 실패. 삭제를 막지는 않는다. */
  unavailable?: string;
};

/** Airflow DAG 상태. AirflowPipelineCard 가 표시한다. */
export type DagStatus = {
  env: AirflowEnv;
  dagId: string;
  /** DAG 가 Airflow 에 아직 등록되지 않았거나 조회 실패 */
  unavailable?: string;
  timetableSummary?: string;
  owners?: string[];
  isPaused?: boolean;
  recentRuns: {
    dagRunId: string;
    state: string;
    logicalDate: string | null;
    startDate: string | null;
    endDate: string | null;
  }[];
  /** Airflow UI 링크 */
  uiUrl?: string;
};

/** 운영계 반영 가능 여부. */
export type PromoteStatus = {
  /** 개발계에만 있고 운영계에 없는 변경이 있는가 */
  hasDiff: boolean;
  changedFiles: { filename: string; status: string }[];
  /** 승격 트리거가 될 PR. 없으면 `/promote` 를 남길 대상이 없다. */
  candidatePr: {
    number: number;
    url: string;
    title: string;
    merged: boolean;
  } | null;
  /** 그 PR 이 바꾼 전체 파일 — 이 DAG 외의 파일도 함께 승격된다. */
  promoteScope: string[];
  devBranch: string;
  prodBranch: string;
};

/**
 * 목록에 보이는 반영 상태. DAG 디렉터리를 개발계·운영계 브랜치에서 비교한다.
 *
 * - `pending`  — 개발계 브랜치에 아직 없다(DAG PR 머지 전)
 * - `dev`      — 개발계에만 있다
 * - `prod`     — 운영계에도 같은 내용으로 있다
 * - `prodStale` — 운영계에도 있지만, 개발계의 새 변경이 아직 운영계에 반영되지 않았다
 */
export type DeployState = 'pending' | 'dev' | 'prod' | 'prodStale';

export const DEPLOY_STATE_LABELS: Record<DeployState, string> = {
  pending: '반영 전',
  dev: '개발계',
  prod: '운영계',
  prodStale: '운영계 · 변경 미반영',
};

export type PromoteResult = {
  alreadyRequested: boolean;
  prUrl: string;
  prNumber: number;
  prTitle: string;
};
